import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { slugify } from '../../utils';
import {
  getPhotoAlbum,
  getPhotos,
  createPhotoAlbum,
  updatePhotoAlbum,
  createPhoto,
  deletePhoto,
  uploadPhoto,
  getPhotoPublicUrl,
  type PhotoAlbum as PhotoAlbumType,
  type Photo,
} from '../../services/api/photos';

export default function PhotoAlbumEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Route is "photos/new" (no :id param) or "photos/albums/:id" — detect "new" by path so we don't stay on Loading
  const isNew = pathname.endsWith('/photos/new');
  const [album, setAlbum] = useState<Partial<PhotoAlbumType>>({ title_en: '', title_th: '', slug: '', sort_order: 0 });
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    if (!id) return;
    setLoading(true);
    Promise.all([getPhotoAlbum(id), getPhotos(id)])
      .then(([a, p]) => {
        setAlbum(a);
        setPhotos(p);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id, isNew]);

  async function handleSave() {
    setSaving(true);
    try {
      if (isNew) {
        const slug = album.slug || slugify(album.title_en || 'gallery');
        const newId = await createPhotoAlbum({ ...album, slug });
        navigate(`/admin/photos/albums/${newId}`, { replace: true });
      } else {
        await updatePhotoAlbum(id!, album);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files?.length || !album.id) return;
    setUploading(true);
    const slug = album.slug || 'gallery';
    try {
      for (let i = 0; i < files.length; i++) {
        const path = await uploadPhoto(slug, files[i]);
        await createPhoto({ photo_album_id: album.id, storage_path: path, sort_order: photos.length + i });
      }
      setPhotos(await getPhotos(album.id));
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleDeletePhoto(photoId: string) {
    if (!confirm(t('admin.removePhoto'))) return;
    await deletePhoto(photoId);
    if (album.id) setPhotos(await getPhotos(album.id));
  }

  if (loading) return <p className="text-[var(--color-text-muted)]">{t('common.loading')}</p>;

  return (
    <div className="admin-page">
      <h1 className="admin-page__title">{isNew ? t('admin.newGallery') : t('admin.editGallery')}</h1>
      {isNew && (
        <p className="admin-section-hint admin-gallery-new-hint">{t('admin.newGalleryStepHint')}</p>
      )}
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <div className="admin-form-group">
          <label>{t('admin.titleEn')}</label>
          <input
            value={album.title_en ?? ''}
            onChange={(e) => setAlbum((a) => ({ ...a, title_en: e.target.value, slug: a?.slug || slugify(e.target.value) }))}
            className="w-full rounded border border-[var(--color-border)] bg-black px-3 py-2 text-[var(--color-text)]"
          />
        </div>
        <div className="admin-form-group">
          <label>{t('admin.titleTh')}</label>
          <input
            value={album.title_th ?? ''}
            onChange={(e) => setAlbum((a) => ({ ...a, title_th: e.target.value }))}
            className="w-full rounded border border-[var(--color-border)] bg-black px-3 py-2 text-[var(--color-text)]"
          />
        </div>
        <div className="admin-form-group">
          <label>{t('admin.slug')}</label>
          <input
            value={album.slug ?? ''}
            onChange={(e) => setAlbum((a) => ({ ...a, slug: e.target.value }))}
            className="w-full rounded border border-[var(--color-border)] bg-black px-3 py-2 text-[var(--color-text)]"
          />
        </div>
      </div>
      <button onClick={handleSave} disabled={saving} className="admin-btn mb-8">
        {saving ? t('admin.saving') : t('admin.saveGallery')}
      </button>

      {!isNew && album.id && (
        <>
          <h2 className="admin-section-title">{t('admin.addPhotosToGallery')}</h2>
          <p className="admin-section-hint">{t('admin.choosePhotosHint')}</p>
          <div className="mb-4">
            <div className="admin-file-upload">
              <label className="admin-file-upload__label">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  disabled={uploading}
                  className="admin-file-upload__input"
                />
                <span className="admin-file-upload__text">
                  {uploading ? t('admin.uploading') : t('admin.choosePhotos')}
                </span>
              </label>
              <span className="admin-file-upload__hint">{t('admin.choosePhotosHint')}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 md:grid-cols-6">
            {photos.map((p) => (
              <div key={p.id} className="relative">
                <img src={getPhotoPublicUrl(p.storage_path)} alt="" className="h-24 w-full object-cover rounded border border-[var(--color-border)]" />
                <button type="button" onClick={() => handleDeletePhoto(p.id)} className="absolute right-1 top-1 rounded bg-red-600/80 px-1 text-xs text-white">{t('common.delete')}</button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
